/**
 * canvas-engine/css/sanitizeHtml.js
 *
 * OWN HTML sanitizer for the Enterprise Canvas Builder.
 *
 * ZERO REUSE: imports NOTHING from admin-console-ui/src/modules/template-builder/
 * or academy-ui/src/template/. Brand-new, self-contained.
 *
 * Pure, deterministic, dependency-free string transform (no React, no DOM
 * parser) so author HTML is sanitized byte-identically in the Console Preview
 * (builder) and the published Reader. (Requirements 4.3, 26.x)
 *
 * Strips:
 *   - <script>...</script> and <style>...</style> blocks (content included)
 *   - dangerous tags (iframe, object, embed, link, meta, base, applet,
 *     frame/frameset, form) — both open and close tags
 *   - HTML comments (which can hide payloads)
 *   - on* inline event handler attributes (onclick, onerror, ...)
 *   - javascript:/vbscript:/data:text/html URLs in URL-bearing attributes
 *
 * NOTE: This is a conservative regex-based sanitizer matching the design's
 * "simple, robust" guidance and the requirement that it run identically with no
 * DOM dependency. It removes known dangerous constructs rather than allow-listing.
 */

/**
 * Remove a paired tag and everything between it (e.g. <script>..</script>).
 * @param {string} html
 * @param {string} tag tag name
 * @returns {string}
 */
function stripPairedTag(html, tag) {
  const re = new RegExp(`<\\s*${tag}\\b[\\s\\S]*?<\\s*/\\s*${tag}\\s*>`, 'gi');
  // Also remove an unmatched/self-terminated opening tag of the same name.
  const openRe = new RegExp(`<\\s*${tag}\\b[^>]*>`, 'gi');
  const closeRe = new RegExp(`<\\s*/\\s*${tag}\\s*>`, 'gi');
  return html.replace(re, '').replace(openRe, '').replace(closeRe, '');
}

// Tags whose open AND close markup is removed (their text content, if any, is
// kept for non content-bearing tags, but for script/style the content is also
// removed via stripPairedTag above).
const DANGEROUS_TAGS = [
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'base',
  'applet',
  'frame',
  'frameset',
  'form',
];

// Attributes that carry URLs and therefore can carry javascript:/vbscript:/data: payloads.
const URL_ATTRS = ['href', 'src', 'xlink:href', 'formaction', 'action', 'background', 'poster'];

// URL schemes we refuse.
const DANGEROUS_URL_SCHEME = /^(?:\s*)(?:javascript|vbscript|data\s*:\s*text\/html)/i;

/**
 * Remove inline event handler attributes (on*) from the markup.
 * @param {string} html
 * @returns {string}
 */
function stripEventHandlers(html) {
  // Matches: whitespace + on<word> = "..." | '...' | bare
  return html.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
}

/**
 * Remove URL-bearing attributes whose value uses a dangerous scheme.
 * @param {string} html
 * @returns {string}
 */
function stripDangerousUrlAttrs(html) {
  let out = html;
  for (let i = 0; i < URL_ATTRS.length; i += 1) {
    const attr = URL_ATTRS[i].replace(':', '\\:');
    const re = new RegExp(`\\s+${attr}\\s*=\\s*("[^"]*"|'[^']*'|[^\\s>]+)`, 'gi');
    out = out.replace(re, (match, rawValue) => {
      // Unwrap surrounding quotes for scheme inspection.
      let value = rawValue;
      if (
        (value.charAt(0) === '"' && value.charAt(value.length - 1) === '"') ||
        (value.charAt(0) === "'" && value.charAt(value.length - 1) === "'")
      ) {
        value = value.slice(1, -1);
      }
      return DANGEROUS_URL_SCHEME.test(value) ? '' : match;
    });
  }
  return out;
}

/**
 * Sanitize author-provided HTML into a safe HTML string.
 * @param {string} html
 * @returns {string} sanitized HTML ('' for non-string/empty input)
 */
function sanitizeHtml(html) {
  if (typeof html !== 'string' || html.length === 0) return '';

  let out = html;

  // 1. Remove comments first (can hide payloads / conditional comments).
  out = out.replace(/<!--[\s\S]*?-->/g, '');

  // 2. Remove script and style blocks (content included).
  out = stripPairedTag(out, 'script');
  out = stripPairedTag(out, 'style');

  // 3. Remove other dangerous tags (open and close markup).
  for (let i = 0; i < DANGEROUS_TAGS.length; i += 1) {
    out = stripPairedTag(out, DANGEROUS_TAGS[i]);
  }

  // 4. Remove inline event handlers (onclick, onerror, ...).
  out = stripEventHandlers(out);

  // 5. Neutralize dangerous URL schemes in URL-bearing attributes.
  out = stripDangerousUrlAttrs(out);

  return out;
}

module.exports = {
  sanitizeHtml,
};
